import { deepMerge } from "../helpers/object-util.js";

/**
 * Base for Highcharts chart options builders. We use the builders to create options for
 * chart components such as axis, labels, tooltip. It allows us to create concrete
 * builders for each component with sensible defaults but also the possibility
 * to easily override values.
 *
 * @example
 *
 * var chart = chartBuilder()
 *   .set('xaxis', dateXAxisBuilder.set('title', 'Months'))
 *   .set('yaxis', amountYXAxis.set('title', 'Earning'))
 *   .build();
 *
 * note: `my.myself` must be assigned to the receiver in subclasses for proper method chaining.
 * For example:
 *      var that = someChartBuilder(spec, my);
 *      my.myself = that;
 * @param {object} spec - Parameter object
 * @param {object} my - protected fields holder
 * @return {{}}
 */
function chartBuilderBase(spec, my) {
	spec ||= {};
	my ||= {};

	const that = {};
	my.myself = that;

	my.options = spec.options || {};

	/**
	 * Configures one of the options
	 *
	 * @param {string} key - Option key
	 * @param {*|chartBuilderBase} value - Option value
	 * @return {chartBuilderBase} returns receiver so that we can chain calls
	 */
	that.set = function (key, value) {
		if (key === undefined || key === null) {
			throw "cannot append null or undefined to object";
		}

		// We allow first argument to be and object to merge
		// If key is an object merge it
		if (typeof key === "object") {
			that.merge(key);
		} else {
			my.setOption(key, value);
		}

		return my.myself;
	};

	/**
	 * Adds a value to a list option
	 *
	 * @param {string} listKey - key for the list
	 * @param {*|chartBuilderBase} value - Value to add
	 * @return {chartBuilderBase} returns receiver so that we can chain calls
	 */
	that.setItem = function (listKey, value) {
		const list = ensureOptionList(listKey);
		list.push(my.buildOption(value));

		return my.myself;
	};

	// TODO: Should be moved to optionsBuilder
	that.setTooltip = function (options) {
		const tooltipOptions = {
			shared: options.shared,
			followPointer: false,
			followTouchMove: false,
			style: {
				fontSize: "12px",
			},
		};

		if (options.formatter) {
			tooltipOptions.formatter = options.formatter;
		}

		that.set("tooltip", tooltipOptions);

		return my.myself;
	};

	/**
	 * Merges given literal options with current options.
	 *
	 * @param {{}} options - Options to merge
	 * @return {chartBuilderBase} returns receiver so that we can chain calls
	 */
	that.merge = function (options) {
		my.options = deepMerge(my.options, options);
		return my.myself;
	};

	/**
	 * Builds the options for the component.
	 *
	 * @return {{}}
	 */
	that.build = function () {
		return my.build();
	};

	my.build = function () {
		return my.buildOptions();
	};

	/**
	 * Checks if option is a builder
	 *
	 * @param {*|chartOptionsBuilder} option - Option to check
	 * @return {boolean}
	 */
	my.isBuilder = function (option) {
		return typeof option === "object" && option.build;
	};

	/**
	 * Builds the option if it is a builder otherwise
	 * returns option as it is.
	 *
	 * @param {object} option - Options to process
	 * @return {*}
	 */
	my.buildOption = function (option) {
		return my.isBuilder(option) ? option.build() : option;
	};

	/**
	 * Sets the value of an option
	 *
	 * @param {string} key - Key to store the value
	 * @param {chartBuilderBase} value - Value for the key
	 * @return {chartBuilderBase} returns the receiver
	 */
	my.setOption = function (key, value) {
		if (key === undefined || key === null) {
			throw "cannot append null or undefined to object";
		}

		// We ignore undefined values for convenience / avoid
		// having to check for undefined values everywhere.
		if (value === undefined) {
			return my.myself;
		}

		my.options[key] = my.buildOption(value);

		return my.myself;
	};

	/**
	 * Builds the option object when we are done configuring it.
	 *
	 * @return {{}}
	 */
	my.buildOptions = function () {
		return my.options;
	};

	/**
	 * Creates list for key if not yet created and returns it.
	 *
	 * @param {string} key - Key to check
	 * @return {Array}
	 */
	function ensureOptionList(key) {
		if (!(key in my.options)) {
			my.options[key] = [];
		}
		return my.options[key];
	}

	return that;
}

/**
 * mergeOptions({a: 1}, {a: 2, b: 1}); //=> {a: 2, b:1}
 *
 * @return {{}}
 */
chartBuilderBase.mergeOptions = function (...params) {
	return deepMerge(...params);
};

export default chartBuilderBase;
